import {
	autoUpdate,
	flip,
	offset,
	shift,
	useFloating,
} from '@floating-ui/react';
import {
	faArrowUp,
	faBracketsCurly,
	faChevronDown,
	faCube,
	faList,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import {
	analyzeTemplateContext,
	generateMustacheSyntax,
	getAvailableVariablesForContext,
	getBlockInsertionOffset,
	type AvailableVariable,
	type TemplateContext,
} from '../../services/templateContextAnalyzer';
import { variableTypeService } from '../../services/variableTypeService';
import { cn } from '../../utils/cn';

interface ContextAwareMergeVariableDropdownProps {
	/** Variables available at root level */
	rootVariables: Array<AvailableVariable>;
	/** Current template content */
	content: string;
	/** Current cursor position in the content */
	cursorPosition: number;
	/** Called when a variable is inserted */
	onInsert: (text: string, cursorOffset?: number) => void;
	/** Additional CSS classes */
	className?: string;
	/** Whether the dropdown is disabled */
	disabled?: boolean;
}

/**
 * A context-aware merge variable dropdown that analyzes the template content
 * at the cursor position and shows the appropriate variables.
 *
 * When you're inside a {{#orderDetails}}...{{/orderDetails}} block,
 * it shows lineItem properties instead of root variables.
 *
 * When selecting an array/object variable, it inserts the block syntax
 * and positions the cursor inside the block.
 */
export const ContextAwareMergeVariableDropdown: React.FC<
	ContextAwareMergeVariableDropdownProps
> = ({
	rootVariables,
	content,
	cursorPosition,
	onInsert,
	className,
	disabled = false,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Floating UI for dropdown positioning
	const { x, y, strategy, refs } = useFloating({
		placement: 'bottom-start',
		middleware: [offset(4), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
	});

	// Analyze context when content or cursor position changes
	const context = useMemo((): TemplateContext => {
		return analyzeTemplateContext(content, cursorPosition, rootVariables);
	}, [content, cursorPosition, rootVariables]);

	// Get available variables for current context
	const availableVariables = useMemo((): Array<AvailableVariable> => {
		return getAvailableVariablesForContext(rootVariables, context);
	}, [rootVariables, context]);

	// Filter variables by search
	const filteredVariables = useMemo(() => {
		if (searchQuery.trim() === '') {
			return availableVariables;
		}

		const query = searchQuery.toLowerCase();

		return availableVariables.filter(
			(v) =>
				v.key.toLowerCase().includes(query) ||
				v.label.toLowerCase().includes(query) ||
				(v.description?.toLowerCase().includes(query) ?? false),
		);
	}, [availableVariables, searchQuery]);

	// Group filtered variables
	const groupedVariables = useMemo(() => {
		const primitives = filteredVariables.filter(
			(v) => !v.isArray && !v.isObject,
		);
		const arrays = filteredVariables.filter((v) => v.isArray);
		const objects = filteredVariables.filter((v) => v.isObject);

		return { primitives, arrays, objects };
	}, [filteredVariables]);

	// Get context label
	const getContextLabel = useCallback((): string => {
		if (context.isRoot) {
			return t('Variables');
		}

		if (context.path.length > 0) {
			const innermost = context.path[context.path.length - 1];

			if (innermost == null) {
				return t('Variables');
			}

			if (innermost.arrayType != null) {
				const typeLabel = variableTypeService.getTypeLabel(innermost.arrayType);

				return `${t('Inside')} ${innermost.name} (${typeLabel})`;
			}

			if (innermost.objectType != null) {
				const typeLabel = variableTypeService.getTypeLabel(
					innermost.objectType,
				);

				return `${t('Inside')} ${innermost.name} (${typeLabel})`;
			}

			return `${t('Inside')} ${innermost.name}`;
		}

		return t('Variables');
	}, [context, t]);

	// Get type suffix for label
	const getTypeSuffix = useCallback((variable: AvailableVariable): string => {
		if (variable.isArray) {
			const typeLabel =
				variable.arrayType != null
					? variableTypeService.getTypeLabel(variable.arrayType)
					: 'items';

			return ` [${typeLabel}]`;
		}

		if (variable.isObject) {
			const typeLabel =
				variable.objectType != null
					? variableTypeService.getTypeLabel(variable.objectType)
					: 'object';

			return ` {${typeLabel}}`;
		}

		return '';
	}, []);

	// Handle selection
	const handleSelect = useCallback(
		(variable: AvailableVariable) => {
			const syntax = generateMustacheSyntax(variable, true);
			const cursorOffset =
				variable.isArray || variable.isObject
					? getBlockInsertionOffset(variable)
					: undefined;

			setIsOpen(false);
			setSearchQuery('');

			// Use setTimeout to ensure the dropdown closes before restoring focus
			setTimeout(() => {
				onInsert(syntax, cursorOffset);
			}, 0);
		},
		[onInsert],
	);

	// Close dropdown when clicking outside
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			const reference = refs.reference.current;
			const floating = refs.floating.current;

			if (
				reference != null &&
				!reference.contains(target) &&
				floating != null &&
				!floating.contains(target)
			) {
				setIsOpen(false);
				setSearchQuery('');
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, refs.reference, refs.floating]);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isOpen && searchInputRef.current != null) {
			searchInputRef.current.focus();
		}
	}, [isOpen]);

	if (rootVariables.length === 0) {
		return null;
	}

	const renderVariableItem = (variable: AvailableVariable) => {
		const isBlock = variable.isArray || variable.isObject;

		return (
			<button
				key={variable.key}
				type="button"
				onClick={() => {
					handleSelect(variable);
				}}
				className={cn(
					'w-full text-left px-3 py-2 text-sm transition-colors rounded',
					'hover:bg-black/5 dark:hover:bg-white/5',
					colors.classes.textPrimary,
				)}
			>
				<div className="flex items-start gap-3">
					<code className="font-mono text-xs text-blue-600 dark:text-blue-400 shrink-0 mt-0.5">
						{isBlock ? `{{#${variable.key}}}` : `{{${variable.key}}}`}
					</code>
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2">
							<span className="font-medium">{t(variable.label)}</span>
							{variable.isArray && (
								<FontAwesomeIcon
									icon={faList}
									className="w-3 h-3 text-blue-500"
								/>
							)}
							{variable.isObject && (
								<FontAwesomeIcon
									icon={faCube}
									className="w-3 h-3 text-purple-500"
								/>
							)}
						</div>
						{variable.description != null && (
							<div
								className={cn('text-xs mt-0.5', colors.classes.textSecondary)}
							>
								{t(variable.description)}
							</div>
						)}
						<div className={cn('text-xs', colors.classes.textSecondary)}>
							{getTypeSuffix(variable)}
						</div>
					</div>
				</div>
			</button>
		);
	};

	const dropdownContent = (
		<div
			ref={refs.setFloating}
			style={{
				position: strategy,
				top: y ?? 0,
				left: x ?? 0,
				width: 400,
				zIndex: 9999,
			}}
			className={cn(
				'rounded-lg shadow-xl border overflow-hidden',
				colors.classes.bgPrimary,
				colors.classes.borderDefault,
			)}
		>
			{/* Context indicator */}
			<div
				className={cn(
					'px-3 py-2 text-xs border-b',
					colors.classes.bgSecondary,
					colors.classes.borderDefault,
				)}
			>
				<div className="flex items-center gap-2">
					<FontAwesomeIcon icon={faBracketsCurly} className="w-3 h-3" />
					<span className={colors.classes.textSecondary}>
						{getContextLabel()}
					</span>
				</div>
			</div>

			{/* Search input */}
			<div className={cn('px-3 py-2 border-b', colors.classes.borderDefault)}>
				<input
					ref={searchInputRef}
					type="text"
					value={searchQuery}
					onChange={(e) => {
						setSearchQuery(e.target.value);
					}}
					placeholder={t('Search variables...')}
					className={cn(
						'w-full px-3 py-1.5 text-sm rounded border',
						colors.classes.bgPrimary,
						colors.classes.textPrimary,
						colors.classes.borderDefault,
						'focus:outline-none focus:ring-2 focus:ring-blue-500',
					)}
				/>
			</div>

			{/* Variables list */}
			<div className="max-h-80 overflow-y-auto p-2">
				{filteredVariables.length === 0 ? (
					<div
						className={cn(
							'px-3 py-4 text-sm text-center',
							colors.classes.textSecondary,
						)}
					>
						{searchQuery.trim() !== ''
							? t('No matching variables')
							: t('No variables available')}
					</div>
				) : (
					<div className="space-y-2">
						{/* Exit block option */}
						{!context.isRoot && context.path.length > 0 && (
							<div className="mb-2 pb-2 border-b border-dashed">
								<button
									type="button"
									onClick={() => {
										setIsOpen(false);
										setSearchQuery('');
									}}
									className={cn(
										'w-full text-left px-3 py-2 text-sm transition-colors rounded',
										'hover:bg-black/5 dark:hover:bg-white/5',
										colors.classes.textSecondary,
									)}
								>
									<div className="flex items-center gap-2">
										<FontAwesomeIcon icon={faArrowUp} className="w-3 h-3" />
										<span>
											{t('Exit')} {context.path[context.path.length - 1]?.name}
										</span>
									</div>
								</button>
							</div>
						)}

						{/* Primitive fields */}
						{groupedVariables.primitives.length > 0 && (
							<div>
								<div
									className={cn(
										'px-3 py-1 text-xs font-medium uppercase tracking-wider',
										colors.classes.textSecondary,
									)}
								>
									{t('Fields')}
								</div>
								{groupedVariables.primitives.map(renderVariableItem)}
							</div>
						)}

						{/* Arrays */}
						{groupedVariables.arrays.length > 0 && (
							<div>
								<div
									className={cn(
										'px-3 py-1 text-xs font-medium uppercase tracking-wider flex items-center gap-1',
										colors.classes.textSecondary,
									)}
								>
									<FontAwesomeIcon icon={faList} className="w-3 h-3" />
									{t('Arrays (Loop)')}
								</div>
								{groupedVariables.arrays.map(renderVariableItem)}
							</div>
						)}

						{/* Objects */}
						{groupedVariables.objects.length > 0 && (
							<div>
								<div
									className={cn(
										'px-3 py-1 text-xs font-medium uppercase tracking-wider flex items-center gap-1',
										colors.classes.textSecondary,
									)}
								>
									<FontAwesomeIcon icon={faCube} className="w-3 h-3" />
									{t('Objects (Block)')}
								</div>
								{groupedVariables.objects.map(renderVariableItem)}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);

	return (
		<div className={cn('inline-block', className)}>
			<button
				ref={refs.setReference}
				type="button"
				onClick={() => {
					setIsOpen(!isOpen);
				}}
				disabled={disabled}
				className={cn(
					'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-colors',
					colors.classes.bgSecondary,
					colors.classes.textPrimary,
					'border',
					colors.classes.borderDefault,
					'hover:opacity-80',
					disabled && 'opacity-50 cursor-not-allowed',
				)}
			>
				<FontAwesomeIcon icon={faBracketsCurly} className="w-3.5 h-3.5" />
				<span>{t('Insert Variable')}</span>
				<FontAwesomeIcon
					icon={faChevronDown}
					className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')}
				/>
			</button>

			{isOpen && createPortal(dropdownContent, document.body)}
		</div>
	);
};
